import { ref, type Ref } from 'vue'
import { useChannelSubscription, type ChannelSubscription } from '../../composables/useChannelSubscription'
import type { AgentRunIndicator } from './runIndicator'
import type { SessionBroadcast, SessionsResponse, StatusBroadcast } from './types'

export interface AgentLiveSync {
  subscribeStatus(): void
  subscribeSessionsList(): void
  // The most recent broadcast flagged notify: true (a web session that ran
  // long, #855) — consumers watch this to pop an in-browser toast.
  lastNotified: Ref<SessionBroadcast | null>
}

// The two cross-device ActionCable subscriptions that keep the session list
// and the active thread's run indicator in sync without polling. Extracted
// (#835) alongside the run-indicator state machine it drives.
export function useAgentLiveSync(deps: {
  sessionsData: Ref<SessionsResponse>
  activeThreadId: Ref<string | null>
  runIndicator: Pick<AgentRunIndicator, 'agentRunning' | 'startRunning' | 'stopRunning'>
  fetchSessions: () => Promise<void>
}): AgentLiveSync {
  const { sessionsData, activeThreadId, runIndicator, fetchSessions } = deps

  // Live cross-device run status: the brain broadcasts `{thread_id,
  // running}` on AgentStatusChannel when a run starts/finishes on ANY device.
  // Reflect it on the matching session so the list's running dot is correct
  // everywhere, not just on the client that started the run. Subscribed once
  // (useChannelSubscription's own guard makes repeat calls a no-op).
  let statusSubscription: ChannelSubscription | null = null
  function subscribeStatus(): void {
    if (statusSubscription) return
    statusSubscription = useChannelSubscription<StatusBroadcast>('AgentStatusChannel', {
      received(data) {
        const match = sessionsData.value.sessions.find((s) => s.thread_id === data.thread_id)
        if (match) match.running = data.running
        if (data.thread_id !== activeThreadId.value) return
        if (data.running) {
          // A run started on ANOTHER device for the session open on THIS one:
          // seed the local indicator immediately instead of leaving the bubble
          // stale until a manual reload (#831).
          if (!runIndicator.agentRunning.value) runIndicator.startRunning()
        } else {
          // Also clear the local "Working…" indicator when the server confirms
          // the active thread's run is done — handles cases where the output
          // channel's final broadcast was missed (WS gap, race on openSession).
          runIndicator.stopRunning()
        }
      },
      // A drop can miss status events; re-pull the list to catch up.
      refetchOnReconnect() {
        void fetchSessions()
      },
    })
  }

  // Live session-list updates: new sessions appear and metadata
  // (name, last_activity_at) updates propagate to every open tab without
  // a full re-fetch. Subscribed once per app lifetime; a WS drop triggers
  // a re-fetch to recover any missed broadcasts.
  const lastNotified = ref<SessionBroadcast | null>(null)
  let sessionsListSubscription: ChannelSubscription | null = null
  function subscribeSessionsList(): void {
    if (sessionsListSubscription) return
    sessionsListSubscription = useChannelSubscription<SessionBroadcast>('AgentSessionsChannel', {
      received(data) {
        // `notify` is a toast signal, not a session-list field — keep it out
        // of the AgentSessionSummary stored in the list.
        const { notify, ...session } = data
        const idx = sessionsData.value.sessions.findIndex((s) => s.thread_id === session.thread_id)
        if (idx !== -1) {
          // Update in-place — spread keeps Vue's reactivity on the array element.
          sessionsData.value.sessions[idx] = { ...sessionsData.value.sessions[idx], ...session }
        } else {
          sessionsData.value.sessions.push(session)
        }
        // Re-sort by last_activity_at (desc) so a bumped EXISTING session
        // moves back to the top instead of staying at its stale index
        // (#833) — mirrors the backend's own `MAX(created_at) DESC`
        // ordering on the initial load; a broadcast is the only thing that
        // can change relative order afterwards.
        sessionsData.value.sessions.sort(
          (a, b) => new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime(),
        )
        if (notify) lastNotified.value = data
      },
      refetchOnReconnect() {
        void fetchSessions()
      },
    })
  }

  return { subscribeStatus, subscribeSessionsList, lastNotified }
}
